import React, {
	useRef,
	useMemo,
	forwardRef,
	useImperativeHandle,
	useState,
	useCallback,
	useLayoutEffect,
} from 'react';
import { useObservableRefValue } from '@chkp/mwc-common';
import RSC from '@chkp/mwc-react-scrollbars-custom';
import * as PropTypes from 'prop-types';
import throttle from 'lodash.throttle';
import ThumbY from './ThumbY';
import styles from './Scrollable.scss';

let Scrollable = (props, ref) => {
	const {
		children,
		forwardedRef,
		onScroll: parentScrollHandler, // Passed by react-window
		style,
		className,
		onUpdate,
		dragTooltip: dragTooltipProp,
		hasScrollXObservable,
		hasScrollYObservable,
		onDragStart,
		onDrag,
		onDragEnd,
		debug,
	} = props;

	const hasScrollX = useObservableRefValue(hasScrollXObservable);
	const hasScrollY = useObservableRefValue(hasScrollYObservable);

	const onUpdateRef = useRef();
	onUpdateRef.current = onUpdate;

	const rsc = useRef();

	// Reset scroll when horizontal scrollbar is disabled
	useLayoutEffect(() => {
		if (!hasScrollX) {
			if (rsc.current) rsc.current.scrollLeft = 0;
		}
	}, [hasScrollX]);

	// Reset scroll when horizontal vertical is disabled
	useLayoutEffect(() => {
		if (!hasScrollY) {
			if (rsc.current) rsc.current.scrollTop = 0;
		}
	}, [hasScrollY]);

	const scrollerProps = useMemo(
		() => ({
			renderer: (rendererProps) => {
				const {
					elementRef,
					onScroll: scrollbarsScrollHandler, // Passed by scrollbars
					...restProps
				} = rendererProps;
				return (
					<span
						{...restProps}
						onScroll={(e) => {
							parentScrollHandler(e);
							scrollbarsScrollHandler(e);
						}}
						ref={(_elementRef) => {
							forwardedRef(_elementRef);
							elementRef(_elementRef);
						}}
					/>
				);
			},
		}),
		[forwardedRef, parentScrollHandler],
	);

	//-----------------------------------------------------------
	// Throttle drag position in order to avoid accidental change of drag target when user releases the mouse.
	const dragScrollState = useRef();
	const dragScrollStateThrottled = useRef();
	const updateDragScrollStateThrottled = useMemo(
		() =>
			throttle(
				() => {
					dragScrollStateThrottled.current = dragScrollState.current;
				},
				100,
				{ leading: false },
			),
		[],
	);

	//-----------------------------------------------------------

	const dragTooltip = useMemo(
		() => dragTooltipProp && (() => dragTooltipProp(dragScrollState.current)),
		[dragTooltipProp],
	);

	const dragTooltipRef = useRef(null);

	//-----------------------------------------------------------

	const thumbYProps = useMemo(
		() => ({
			renderer: (rendererProps) => {
				return (
					<ThumbY
						{...rendererProps}
						className={styles.thumbY}
						// Thumb renderer is not called during detached dragging (fix it?), thus we pass tooltip function via ref
						dragTooltipRef={dragTooltipRef}
					/>
				);
			},
			onDragStart: () => {
				dragTooltipRef.current = dragTooltip;
				dragScrollState.current = rsc.current && rsc.current.getScrollState();
				onDragStart && onDragStart(dragScrollState.current);
			},
			onDrag: () => {
				dragTooltipRef.current = dragTooltip; // optional, just for a case
				dragScrollState.current = rsc.current && rsc.current.getScrollState();
				updateDragScrollStateThrottled();
				onDrag && onDrag(dragScrollState.current);
			},
			onDragEnd: () => {
				onDragEnd && onDragEnd(dragScrollStateThrottled.current);
				dragTooltipRef.current = null;
				dragScrollState.current = null;
				dragScrollStateThrottled.current = null;
			},
		}),
		[
			dragTooltip,
			onDrag,
			onDragEnd,
			onDragStart,
			updateDragScrollStateThrottled,
		],
	);

	//--------------------------------------------------------------

	// eslint-disable-next-line react-hooks/rules-of-hooks
	// debug && useDebugger('Scrollable',{
	// 	...props,
	// 	scrollerProps,
	// 	dragTooltip,
	// 	thumbYProps,
	// }, true);

	//--------------------------------------------------------------

	useImperativeHandle(
		ref,
		() => ({
			detachScroll: () => rsc.current && rsc.current.detachScroll(),
			attachScroll: () => rsc.current && rsc.current.attachScroll(),
		}),
		[],
	);

	const trackYHeight = `calc(100% - ${hasScrollX ? 10 : 0}px)`;
	const trackXWidth = `calc(100% - ${hasScrollY ? 10 : 0}px)`;

	//--------------------------------------------------------------

	return (
		<RSC
			ref={rsc}
			className={className}
			style={style}
			onUpdate={onUpdateRef.current}
			scrollerProps={scrollerProps}
			thumbYProps={thumbYProps}
			trackYProps={{
				className: styles.trackY,
				style: { height: trackYHeight, top: 0 },
			}}
			thumbXProps={{ className: styles.thumbX }}
			trackXProps={{
				className: styles.trackX,
				style: { width: trackXWidth, left: 0 },
			}}
			permanentTrackX={hasScrollX}
			permanentTrackY={hasScrollY}
			noScrollX={!hasScrollX}
			noScrollY={!hasScrollY}
		>
			{children}
		</RSC>
	);
};

Scrollable = forwardRef(Scrollable);
Scrollable.propTypes = {
	forwardedRef: PropTypes.func,
	onScroll: PropTypes.func, // Passed by react-window
	style: PropTypes.object,
	className: PropTypes.string,
	onUpdate: PropTypes.func,
	dragTooltip: PropTypes.func,
	debug: PropTypes.bool,
};

export { Scrollable };
